import React, { useState } from 'react';
import ProtectedImage from './ProtectedImage';
import ProtectedVideo from './ProtectedVideo';
import ProtectedVideoThumbnail from './ProtectedVideoThumbnail';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/**
 * Sortable media item for drag-and-drop
 */
const SortableMediaItem = ({ item, onView, onDelete, layoutCode, editMode }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isThumbs = layoutCode === 'GALLERY_THUMBS';
  const isTiles = layoutCode === 'GALLERY_TILES';

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`relative aspect-square ${isThumbs ? 'rounded' : ''} overflow-hidden bg-gray-200 dark:bg-gray-700 group ${editMode ? 'cursor-move' : 'cursor-pointer'}`}
    >
      <div
        onClick={(e) => {
          if (onView) {
            e.stopPropagation();
            onView(item);
          }
        }}
        className="w-full h-full hover:opacity-80 transition-opacity"
      >
        {item.kind_code === 'IMAGE' ? (
          <ProtectedImage
            uuid={item.thumbnail_uuid || item.uuid}
            alt={item.storage_key}
            className="w-full h-full object-cover"
          />
        ) : (
          <ProtectedVideoThumbnail
            uuid={item.uuid}
            className="w-full h-full"
          />
        )}
      </div>
      {editMode && onDelete && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(item.id);
          }}
          className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700 z-10"
          title="Delete"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      )}
    </div>
  );
};

/**
 * Shared Gallery Renderer Component
 * Renders galleries with proper layouts and optional edit controls
 */
const GalleryRenderer = ({
  media,
  layout,
  paragraphLayoutCode = null,
  editMode = false,
  onLayoutChange = null,
  onAddMedia = null,
  onDeleteMedia = null,
  onDeleteGallery = null,
  onViewMedia = null,
  onReorderMedia = null,
  availableLayouts = [],
  uploading = false,
  className = ''
}) => {
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [localMedia, setLocalMedia] = useState(media);

  // Update local media when prop changes
  React.useEffect(() => {
    setLocalMedia(media);
  }, [media]);

  // Drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Handle drag end for media reordering
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = localMedia.findIndex(m => m.id === active.id);
    const newIndex = localMedia.findIndex(m => m.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    // Update local state optimistically
    const reordered = arrayMove(localMedia, oldIndex, newIndex);
    setLocalMedia(reordered);

    // Call callback with new order
    if (onReorderMedia) {
      const mediaOrder = reordered.map(m => m.id);
      onReorderMedia(mediaOrder);
    }
  };

  if (!media || media.length === 0) {
    if (editMode) {
      return (
        <div className={`surface rounded-lg p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 ${className}`}>
          <p className="text-secondary text-center text-sm mb-3">No media uploaded</p>
          {onAddMedia && (
            <label className="block">
              <input
                type="file"
                multiple
                accept="image/*,video/*"
                onChange={(e) => {
                  const files = Array.from(e.target.files);
                  if (files.length > 0) {
                    onAddMedia(files);
                    e.target.value = '';
                  }
                }}
                className="hidden"
              />
              <div className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 text-center text-sm cursor-pointer">
                + Upload Media
              </div>
            </label>
          )}
        </div>
      );
    }
    return null;
  }

  const resolvedLayout = layout || 'GALLERY_THUMBS';

  // Check if gallery is in a constrained width (FLOAT or CLEAR layout)
  const isConstrained = paragraphLayoutCode &&
    (paragraphLayoutCode.includes('FLOAT') || paragraphLayoutCode.includes('CLEAR'));

  // Render layout selection controls (if in edit mode)
  const renderLayoutControls = () => {
    if (!editMode || !onLayoutChange || availableLayouts.length === 0) return null;

    return (
      <div className="flex justify-between items-center mb-3">
        <div className="flex gap-1">
          {availableLayouts.map((layoutOption) => {
          // Determine which icon to show
          let icon;
          if (layoutOption.code === 'GALLERY_THUMBS') {
            icon = (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            );
          } else if (layoutOption.code === 'GALLERY_TILES') {
            icon = (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M3 3h8v8H3V3zm10 0h8v8h-8V3zM3 13h8v8H3v-8zm10 0h8v8h-8v-8z"/>
              </svg>
            );
          } else if (layoutOption.code === 'GALLERY_CAROUSEL') {
            icon = (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <rect x="3" y="6" width="18" height="12" rx="2" strokeWidth="2"/>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12l-3 2V10l3 2z" />
              </svg>
            );
          } else {
            // Fallback to text for unknown layouts
            return (
              <button
                key={layoutOption.code}
                onClick={() => onLayoutChange(layoutOption.code)}
                className={`px-3 py-1 text-xs rounded transition-colors ${
                  resolvedLayout === layoutOption.code
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-secondary hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
                title={layoutOption.label}
              >
                {layoutOption.label}
              </button>
            );
          }

          return (
            <button
              key={layoutOption.code}
              onClick={() => onLayoutChange(layoutOption.code)}
              className={`p-2 rounded transition-colors ${
                resolvedLayout === layoutOption.code
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
              title={layoutOption.label}
            >
              {icon}
            </button>
          );
          })}
        </div>
        {editMode && onDeleteGallery && (
          <button
            onClick={onDeleteGallery}
            className="p-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
            title="Delete gallery"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        )}
      </div>
    );
  };

  // Render add media button (if in edit mode) - circular (+) button bottom right
  const renderAddMediaButton = () => {
    if (!editMode || !onAddMedia) return null;

    if (uploading) {
      return (
        <div className="mt-3">
          <div className="text-sm text-secondary mb-2">Uploading...</div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div className="bg-primary-600 h-2 rounded-full transition-all animate-pulse" style={{ width: '100%' }}></div>
          </div>
        </div>
      );
    }

    return (
      <div className="flex justify-end mt-3">
        <label className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-600 text-white hover:bg-primary-700 cursor-pointer transition-colors">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <input
            type="file"
            multiple
            accept="image/*,video/*"
            onChange={(e) => {
              const files = Array.from(e.target.files);
              if (files.length > 0) {
                onAddMedia(files);
                e.target.value = '';
              }
            }}
            className="hidden"
          />
        </label>
      </div>
    );
  };

  // Render delete button on media item (if in edit mode)
  const renderDeleteButton = (item) => {
    if (!editMode || !onDeleteMedia) return null;

    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDeleteMedia(item.id);
        }}
        className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700 z-10"
        title="Delete"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      </button>
    );
  };

  // THUMBNAILS Layout - Grid with gaps + drag-and-drop
  if (resolvedLayout === 'GALLERY_THUMBS') {
    const content = (
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-4">
        {(editMode ? localMedia : media).map((item) => (
          <SortableMediaItem
            key={item.id}
            item={item}
            layoutCode="GALLERY_THUMBS"
            editMode={editMode}
            onView={onViewMedia}
            onDelete={editMode ? onDeleteMedia : null}
          />
        ))}
      </div>
    );

    return (
      <div className={className}>
        {renderLayoutControls()}
        {editMode && onReorderMedia ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={localMedia.map(m => m.id)}
              strategy={rectSortingStrategy}
            >
              {content}
            </SortableContext>
          </DndContext>
        ) : (
          content
        )}
        {renderAddMediaButton()}
      </div>
    );
  }

  // TILES Layout - Smart layout based on context and item count
  if (resolvedLayout === 'GALLERY_TILES') {
    // If constrained (FLOAT/CLEAR), stack all media vertically
    if (isConstrained) {
      return (
        <div className={className}>
          {renderLayoutControls()}
          <div className="space-y-0 mb-4">
            {media.map((item) => (
              <div
                key={item.id}
                className="relative w-full bg-gray-200 dark:bg-gray-700 cursor-pointer hover:opacity-80 transition-opacity group"
                onClick={() => onViewMedia && onViewMedia(item)}
              >
                {item.kind_code === 'IMAGE' ? (
                  <ProtectedImage
                    uuid={item.banner_uuid || item.uuid}
                    alt={item.storage_key}
                    className="w-full h-auto"
                  />
                ) : (
                  <ProtectedVideoThumbnail
                    uuid={item.uuid}
                    className="w-full h-auto"
                  />
                )}
                {editMode && onDeleteMedia && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteMedia(item.id);
                    }}
                    className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700 z-10"
                    title="Delete"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>
          {renderAddMediaButton()}
        </div>
      );
    }

    // Full width context (ABOVE/BELOW) - smart layout based on count
    const isOdd = media.length % 2 === 1;
    const rows = [];

    if (isOdd) {
      // Odd number: First item is "hero" (full width), rest shown 2 by 2
      rows.push(
        <div
          key={media[0].id}
          className="relative w-full bg-gray-200 dark:bg-gray-700 cursor-pointer hover:opacity-80 transition-opacity group"
          onClick={() => onViewMedia && onViewMedia(media[0])}
        >
          {media[0].kind_code === 'IMAGE' ? (
            <ProtectedImage
              uuid={media[0].banner_uuid || media[0].uuid}
              alt={media[0].storage_key}
              className="w-full h-auto"
            />
          ) : (
            <ProtectedVideoThumbnail
              uuid={media[0].uuid}
              className="w-full h-auto"
            />
          )}
          {editMode && onDeleteMedia && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDeleteMedia(media[0].id);
              }}
              className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700 z-10"
              title="Delete"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
        </div>
      );

      // Remaining items in pairs
      for (let i = 1; i < media.length; i += 2) {
        rows.push(
          <div key={`pair-${i}`} className="grid grid-cols-2 gap-0">
            <div
              className="relative bg-gray-200 dark:bg-gray-700 cursor-pointer hover:opacity-80 transition-opacity group"
              onClick={() => onViewMedia && onViewMedia(media[i])}
            >
              {media[i].kind_code === 'IMAGE' ? (
                <ProtectedImage
                  uuid={media[i].banner_uuid || media[i].uuid}
                  alt={media[i].storage_key}
                  className="w-full h-auto"
                />
              ) : (
                <ProtectedVideoThumbnail
                  uuid={media[i].uuid}
                  className="w-full h-auto"
                />
              )}
              {editMode && onDeleteMedia && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteMedia(media[i].id);
                  }}
                  className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700 z-10"
                  title="Delete"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
              )}
            </div>
            <div
              className="relative bg-gray-200 dark:bg-gray-700 cursor-pointer hover:opacity-80 transition-opacity group"
              onClick={() => onViewMedia && onViewMedia(media[i + 1])}
            >
              {media[i + 1].kind_code === 'IMAGE' ? (
                <ProtectedImage
                  uuid={media[i + 1].banner_uuid || media[i + 1].uuid}
                  alt={media[i + 1].storage_key}
                  className="w-full h-auto"
                />
              ) : (
                <ProtectedVideoThumbnail
                  uuid={media[i + 1].uuid}
                  className="w-full h-auto"
                />
              )}
              {editMode && onDeleteMedia && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteMedia(media[i + 1].id);
                  }}
                  className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700 z-10"
                  title="Delete"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
              )}
            </div>
          </div>
        );
      }
    } else {
      // Even number: All items shown 2 by 2
      for (let i = 0; i < media.length; i += 2) {
        rows.push(
          <div key={`pair-${i}`} className="grid grid-cols-2 gap-0">
            <div
              className="relative bg-gray-200 dark:bg-gray-700 cursor-pointer hover:opacity-80 transition-opacity group"
              onClick={() => onViewMedia && onViewMedia(media[i])}
            >
              {media[i].kind_code === 'IMAGE' ? (
                <ProtectedImage
                  uuid={media[i].banner_uuid || media[i].uuid}
                  alt={media[i].storage_key}
                  className="w-full h-auto"
                />
              ) : (
                <ProtectedVideoThumbnail
                  uuid={media[i].uuid}
                  className="w-full h-auto"
                />
              )}
              {editMode && onDeleteMedia && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteMedia(media[i].id);
                  }}
                  className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700 z-10"
                  title="Delete"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
              )}
            </div>
            <div
              className="relative bg-gray-200 dark:bg-gray-700 cursor-pointer hover:opacity-80 transition-opacity group"
              onClick={() => onViewMedia && onViewMedia(media[i + 1])}
            >
              {media[i + 1].kind_code === 'IMAGE' ? (
                <ProtectedImage
                  uuid={media[i + 1].banner_uuid || media[i + 1].uuid}
                  alt={media[i + 1].storage_key}
                  className="w-full h-auto"
                />
              ) : (
                <ProtectedVideoThumbnail
                  uuid={media[i + 1].uuid}
                  className="w-full h-auto"
                />
              )}
              {editMode && onDeleteMedia && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteMedia(media[i + 1].id);
                  }}
                  className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700 z-10"
                  title="Delete"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
              )}
            </div>
          </div>
        );
      }
    }

    return (
      <div className={className}>
        {renderLayoutControls()}
        <div className="space-y-0 mb-4">{rows}</div>
        {renderAddMediaButton()}
      </div>
    );
  }

  // CAROUSEL Layout - Full slideshow with navigation
  if (resolvedLayout === 'GALLERY_CAROUSEL') {
    const currentIndex = carouselIndex;

    return (
      <div className={className}>
        {renderLayoutControls()}
        <div className="mb-4">
          <div className="relative bg-gray-900 rounded-lg overflow-hidden" style={{ height: '400px' }}>
            {/* Current slide */}
            <div className="absolute inset-0 flex items-center justify-center group">
              {media[currentIndex]?.kind_code === 'IMAGE' ? (
                <ProtectedImage
                  uuid={media[currentIndex]?.uuid}
                  alt="Carousel image"
                  className="max-w-full max-h-full object-contain cursor-pointer"
                  onClick={() => onViewMedia && onViewMedia(media[currentIndex])}
                />
              ) : (
                <ProtectedVideo
                  uuid={media[currentIndex]?.uuid}
                  className="max-w-full max-h-full object-contain cursor-pointer"
                  controls={true}
                  onClick={() => onViewMedia && onViewMedia(media[currentIndex])}
                />
              )}
              {renderDeleteButton(media[currentIndex])}
            </div>

            {/* Navigation arrows - only show if more than 1 item */}
            {media.length > 1 && (
              <>
                <button
                  onClick={() => setCarouselIndex(currentIndex === 0 ? media.length - 1 : currentIndex - 1)}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black bg-opacity-50 hover:bg-opacity-75 text-white rounded-full flex items-center justify-center transition-all"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={() => setCarouselIndex((currentIndex + 1) % media.length)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black bg-opacity-50 hover:bg-opacity-75 text-white rounded-full flex items-center justify-center transition-all"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </>
            )}
          </div>

          {/* Slide indicators - below the carousel */}
          <div className="flex justify-center gap-2 mt-3">
            {media.map((_, index) => (
              <button
                key={index}
                onClick={() => setCarouselIndex(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  currentIndex === index
                    ? 'bg-primary-600 w-8'
                    : 'bg-primary-600 bg-opacity-50'
                }`}
              />
            ))}
          </div>
        </div>
        {renderAddMediaButton()}
      </div>
    );
  }

  return null;
};

export default GalleryRenderer;
